import {NewsArticle, NewsPage} from "@/hooks/useNews.ts";

export type PageResult<T = any> = {
    items: T[];
    total: number;
    page: number;       // 0-based
    size: number;
    totalPages: number;
};

type SpringPage<T> = {
    content?: T[];
    page?: {
        number?: number;
        size?: number;
        totalElements?: number;
        totalPages?: number;
    };
};

type PlainPage<T> = {
    items?: T[];
    total?: number;
    page?: number;
    size?: number;
    totalPages?: number;
};

type NormalizeDefaults = {
    page?: number;   // default 0
    size?: number;   // default 10
};

const DFLT: Required<NormalizeDefaults> = { page: 0, size: 10 };

function isNumber(n: unknown): n is number {
    return typeof n === "number" && !Number.isNaN(n);
}

function clampNonNegative(n: unknown, fallback: number) {
    return isNumber(n) && n >= 0 ? n : fallback;
}

function coerceItems<T>(arr: unknown): T[] {
    return Array.isArray(arr) ? (arr as T[]) : [];
}

/**
 * Normalize any backend page response into a PageResult<T>.
 * Supports Spring Pageable and a plain { items, total, page, size, totalPages } shape.
 */
export function normalizePage<T = any>(
    data: SpringPage<T> | PlainPage<T> | undefined | null,
    defaults: NormalizeDefaults = DFLT
): PageResult<T> {
    const defPage = clampNonNegative(defaults.page, DFLT.page);
    const defSize = clampNonNegative(defaults.size, DFLT.size);

    if (!data || typeof data !== "object") {
        return { items: [], total: 0, page: defPage, size: defSize, totalPages: 0 };
    }

    // Spring Pageable shape
    if ("content" in data || "page" in data) {
        const pg = (data as SpringPage<T>).page ?? {};
        const items = coerceItems<T>((data as SpringPage<T>).content);
        const size = clampNonNegative(pg.size, defSize);
        const total = clampNonNegative(pg.totalElements, 0);
        const page = clampNonNegative(pg.number, defPage);
        const totalPages = clampNonNegative(pg.totalPages, 0);
        return { items, total, page, size, totalPages };
    }

    // Plain page shape
    const p = data as PlainPage<T>;
    const items = coerceItems<T>(p.items);
    const size = clampNonNegative(p.size, defSize);
    const total = clampNonNegative(p.total, 0);
    const page = clampNonNegative(p.page, defPage);
    const totalPages = clampNonNegative(p.totalPages, size > 0 ? Math.ceil(total / size) : 0);
    return { items, total, page, size, totalPages };
}

/** Handy helper to create an empty page with chosen page/size */
export function emptyPage<T = any>(page = DFLT.page, size = DFLT.size): PageResult<T> {
    return { items: [], total: 0, page, size, totalPages: 0 };
}
